import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, UserX, Loader2, AlertCircle } from 'lucide-react';
import { FONTS } from '../../lib/theme';
import { getBlockedUsers, unblockUser } from '../../lib/communityApi';

/**
 * Blocked users management panel.
 * Shows a list of users the current user has blocked, with unblock capability.
 * Designed to sit alongside PrivacySettings in the Community tab.
 */
const BlockedUsersList = () => {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unblocking, setUnblocking] = useState(null); // userId being unblocked

  const loadBlocked = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getBlockedUsers();
      setBlocked(result.blocked || []);
    } catch (err) {
      console.error('[BlockedUsersList] Load failed:', err);
      setError('Failed to load blocked users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBlocked();
  }, [loadBlocked]);

  const handleUnblock = async (user) => {
    if (unblocking) return;
    setUnblocking(user.id);
    try {
      await unblockUser(user.id);
      setBlocked(prev => prev.filter(b => b.id !== user.id));
    } catch (err) {
      console.error('[BlockedUsersList] Unblock failed:', err);
      setError(err.message || 'Failed to unblock user');
    } finally {
      setUnblocking(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
          <UserX className="w-5 h-5 text-red-500" />
        </div>
        <div>
          <h2 className="text-xl text-gray-900" style={FONTS.headingBold}>
            Blocked Users
          </h2>
          <p className="text-xs text-gray-500 mt-0.5" style={FONTS.body}>
            Blocked users cannot message you or see your profile
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600" style={FONTS.body}>{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : blocked.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center py-8 text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500" style={FONTS.body}>
            No blocked users
          </p>
          <p className="text-xs text-gray-400 mt-1" style={FONTS.body}>
            Users you block will appear here
          </p>
        </div>
      ) : (
        /* User list */
        <div className="space-y-2">
          {blocked.map(user => {
            const initial = (user.display_name || '?').charAt(0).toUpperCase();
            const isUnblocking = unblocking === user.id;

            return (
              <div
                key={user.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition-colors"
              >
                {/* Avatar */}
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.display_name || 'User'}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm text-gray-500" style={FONTS.headingBold}>
                      {initial}
                    </span>
                  </div>
                )}

                {/* Name */}
                <span
                  className="flex-1 text-sm text-gray-800 truncate"
                  style={FONTS.bodyMedium}
                >
                  {user.display_name || 'Unknown User'}
                </span>

                {/* Unblock button */}
                <button
                  onClick={() => handleUnblock(user)}
                  disabled={isUnblocking}
                  className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  style={FONTS.bodyBold}
                >
                  {isUnblocking ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : null}
                  Unblock
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BlockedUsersList;
